import { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import {
  COMMON_CURRENCIES,
  DEFAULT_SYSTEM_NAME,
  P,
  currencySymbolFor,
  decimalPlacesFor,
  type BusinessSettingsPayload,
} from '@stockpilot/shared';
import {
  useFeaturesQuery, useOrganizationQuery, useSettingsQuery, useToggleFeatureMutation,
  useUpdateOrganizationMutation, useUpdateSettingsMutation,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import {
  Alert, Button, Card, CardBody, CardHeader, Checkbox, Input, LoadingState, PageHeader,
  Select,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { AppearanceCard } from '@/components/AppearanceCard';
import { PrinterCard } from '@/components/PrinterCard';

/** Only optional modules are listed; core modules cannot be switched off. */
const OPTIONAL_FEATURES = [
  ['POS', 'Point of Sale', 'A till with a cash drawer, split tenders and parked carts'],
  ['MULTI_BRANCH', 'Multi-branch', 'Operate more than one branch'],
  ['MULTI_WAREHOUSE', 'Multi-warehouse', 'Several warehouses and transfers between them'],
  ['BATCH_EXPIRY', 'Batches & expiry', 'Batch numbers, expiry dates and alerts'],
  ['SERIAL_NUMBERS', 'Serial numbers', 'Serial and IMEI tracking'],
  ['WARRANTY', 'Warranty', 'Warranty registration and claims'],
  ['PROMOTIONS', 'Promotions', 'Discount rules and campaigns'],
  ['ADVANCED_PRICING', 'Advanced pricing', 'Price lists, tiers and customer pricing'],
  ['MANUFACTURING', 'Manufacturing', 'Bills of materials and production'],
  ['RESTAURANT', 'Restaurant', 'Menus, recipes, tables and kitchen'],
  ['DELIVERY', 'Delivery', 'Delivery orders and drivers'],
] as const;

export function SettingsPage() {
  const { data: settings, isLoading } = useSettingsQuery();
  const features = useFeaturesQuery();
  const [updateSettings, updateState] = useUpdateSettingsMutation();
  const organization = useOrganizationQuery();
  const [updateOrganization, orgState] = useUpdateOrganizationMutation();
  const [toggleFeature] = useToggleFeatureMutation();

  const [form, setForm] = useState<Partial<BusinessSettingsPayload>>({});
  const [saved, setSaved] = useState(false);

  /**
   * The business's own identity — what it is called and how to reach it. This
   * is what heads every receipt and invoice, so it lives here rather than
   * being fixed at sign-up.
   */
  const [profile, setProfile] = useState<Record<string, string>>({});
  const [profileSaved, setProfileSaved] = useState(false);

  useEffect(() => {
    const row = organization.data as Record<string, unknown> | undefined;
    if (!row) return;
    setProfile({
      name: String(row.name ?? ''),
      legalName: String(row.legalName ?? ''),
      email: String(row.email ?? ''),
      phone: String(row.phone ?? ''),
      website: String(row.website ?? ''),
      addressLine1: String(row.addressLine1 ?? ''),
      addressLine2: String(row.addressLine2 ?? ''),
      city: String(row.city ?? ''),
      taxNumber: String(row.taxNumber ?? ''),
    });
  }, [organization.data]);

  const saveProfile = async (): Promise<void> => {
    setProfileSaved(false);
    const result = await updateOrganization({
      name: profile.name,
      legalName: profile.legalName || undefined,
      email: profile.email || undefined,
      phone: profile.phone || undefined,
      website: profile.website || undefined,
      addressLine1: profile.addressLine1 || undefined,
      addressLine2: profile.addressLine2 || undefined,
      city: profile.city || undefined,
      taxNumber: profile.taxNumber || undefined,
    })
      .unwrap()
      .catch(() => null);

    if (result) setProfileSaved(true);
  };

  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  const update = <K extends keyof BusinessSettingsPayload>(
    key: K,
    value: BusinessSettingsPayload[K],
  ): void => {
    setSaved(false);
    setForm((current) => ({ ...current, [key]: value }));
  };

  /**
   * Picking a currency brings its usual symbol and decimal places with it —
   * UGX means USh and whole shillings — but both stay editable underneath, so a
   * business that writes its amounts differently is never overruled.
   */
  const updateCurrency = (code: string): void => {
    setSaved(false);
    setForm((current) => ({
      ...current,
      currency: code,
      currencySymbol: currencySymbolFor(code),
      decimalPlaces: decimalPlacesFor(code),
    }));
  };

  const save = async (): Promise<void> => {
    const result = await updateSettings({
      systemName: form.systemName,
      currency: form.currency,
      currencySymbol: form.currencySymbol,
      currencyPosition: form.currencyPosition,
      decimalPlaces: form.decimalPlaces,
      dateFormat: form.dateFormat,
      timezone: form.timezone,
      locale: form.locale,
      taxInclusivePricing: form.taxInclusivePricing,
      costingMethod: form.costingMethod,
      allowNegativeStock: form.allowNegativeStock,
      requireStockTransferApproval: form.requireStockTransferApproval,
      requirePurchaseApproval: form.requirePurchaseApproval,
      lowStockAlertsEnabled: form.lowStockAlertsEnabled,
      reminderEmails: form.reminderEmails ?? [],
      fiscalYearStartMonth: form.fiscalYearStartMonth,
      receiptHeader: form.receiptHeader,
      receiptFooter: form.receiptFooter,
      receiptTemplate: form.receiptTemplate,
    })
      .unwrap()
      .catch(() => null);

    if (result) setSaved(true);
  };

  if (isLoading || !settings) return <LoadingState label="Loading settings…" />;

  const enabled = new Set(features.data?.enabled ?? []);
  const currencyChanged = !!form.currency && form.currency !== settings.currency;
  const currencyOptions = COMMON_CURRENCIES.includes(settings.currency)
    ? COMMON_CURRENCIES
    : [settings.currency, ...COMMON_CURRENCIES];

  return (
    <>
      <PageHeader
        title="Business settings"
        description="Currency, tax, inventory rules and approval policy for this business."
        action={
          <Button icon={Save} onClick={() => void save()} loading={updateState.isLoading}>
            Save changes
          </Button>
        }
      />

      {saved && <div className="mb-4"><Alert tone="success">Your settings have been saved.</Alert></div>}
      {updateState.error && (
        <div className="mb-4"><Alert tone="danger">{apiErrorMessage(updateState.error)}</Alert></div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader
            title="The business"
            description="Its name and details, which head every receipt and invoice."
            action={
              <Can permission={P.ORGANIZATION_UPDATE}>
                <Button
                  size="sm"
                  icon={Save}
                  loading={orgState.isLoading}
                  disabled={!profile.name}
                  onClick={() => void saveProfile()}
                >
                  Save
                </Button>
              </Can>
            }
          />
          <CardBody className="space-y-4">
            {profileSaved && <Alert tone="success">The business details have been saved.</Alert>}
            {orgState.error && <Alert tone="danger">{apiErrorMessage(orgState.error)}</Alert>}

            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Business name"
                required
                value={profile.name ?? ''}
                onChange={(event) => setProfile({ ...profile, name: event.target.value })}
                hint="Shown at the top of every receipt."
              />
              <Input
                label="Registered name"
                value={profile.legalName ?? ''}
                onChange={(event) => setProfile({ ...profile, legalName: event.target.value })}
                hint="If it differs from the trading name."
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <Input
                label="Phone"
                value={profile.phone ?? ''}
                onChange={(event) => setProfile({ ...profile, phone: event.target.value })}
              />
              <Input
                label="Email"
                type="email"
                value={profile.email ?? ''}
                onChange={(event) => setProfile({ ...profile, email: event.target.value })}
              />
              <Input
                label="Tax number"
                value={profile.taxNumber ?? ''}
                onChange={(event) => setProfile({ ...profile, taxNumber: event.target.value })}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <Input
                label="Address"
                value={profile.addressLine1 ?? ''}
                onChange={(event) => setProfile({ ...profile, addressLine1: event.target.value })}
              />
              <Input
                label="Address line 2"
                value={profile.addressLine2 ?? ''}
                onChange={(event) => setProfile({ ...profile, addressLine2: event.target.value })}
              />
              <Input
                label="Town or city"
                value={profile.city ?? ''}
                onChange={(event) => setProfile({ ...profile, city: event.target.value })}
              />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Naming"
            description="What this business calls the system."
          />
          <CardBody className="space-y-4">
            <Input
              label="System name"
              value={form.systemName ?? ''}
              onChange={(event) => update('systemName', event.target.value)}
              placeholder={DEFAULT_SYSTEM_NAME}
              hint={`Shown in the sidebar and the browser tab. Leave it blank for ${DEFAULT_SYSTEM_NAME}.`}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Receipts"
            description="What a printed receipt says around the sale itself."
          />
          <CardBody className="space-y-4">
            <Select
              label="Paper"
              value={form.receiptTemplate ?? 'THERMAL_80'}
              onChange={(event) => update('receiptTemplate', event.target.value)}
              hint="A till roll is 80mm wide. A4 is for a printed page."
            >
              <option value="THERMAL_80">Till roll (80mm)</option>
              <option value="A4">A4 page</option>
            </Select>
            <Input
              label="Header"
              value={form.receiptHeader ?? ''}
              onChange={(event) => update('receiptHeader', event.target.value)}
              hint="Shown under the business name and address."
            />
            <Input
              label="Footer"
              value={form.receiptFooter ?? ''}
              onChange={(event) => update('receiptFooter', event.target.value)}
              hint="The last line — returns policy, thanks, opening hours."
            />
            <p className="text-xs text-muted">
              The business name, address, phone and tax number come from the business profile,
              so a receipt says the same thing as every other document.
            </p>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Money & locale" description="How amounts and dates are shown everywhere." />
          <CardBody className="space-y-4">
            {currencyChanged && (
              <Alert tone="warning">
                Saving changes how money is shown from {settings.currency} to {form.currency}.
                Amounts already recorded keep their figures — nothing is converted — so change
                this only if {settings.currency} was wrong in the first place.
              </Alert>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                label="Currency"
                value={form.currency ?? ''}
                onChange={(event) => updateCurrency(event.target.value)}
              >
                {currencyOptions.map((code) => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </Select>
              <Input
                label="Symbol"
                value={form.currencySymbol ?? ''}
                onChange={(event) => update('currencySymbol', event.target.value)}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                label="Symbol position"
                value={form.currencyPosition ?? 'BEFORE'}
                onChange={(event) => update('currencyPosition', event.target.value as 'BEFORE' | 'AFTER')}
              >
                <option value="BEFORE">Before the amount ($100)</option>
                <option value="AFTER">After the amount (100$)</option>
              </Select>
              <Input
                label="Decimal places"
                type="number"
                min={0}
                max={6}
                value={form.decimalPlaces ?? 2}
                onChange={(event) => update('decimalPlaces', Number(event.target.value))}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Time zone"
                value={form.timezone ?? ''}
                onChange={(event) => update('timezone', event.target.value)}
              />
              <Input
                label="Date format"
                value={form.dateFormat ?? ''}
                onChange={(event) => update('dateFormat', event.target.value)}
              />
            </div>
            <Input
              label="Fiscal year starts in month"
              type="number"
              min={1}
              max={12}
              value={form.fiscalYearStartMonth ?? 1}
              onChange={(event) => update('fiscalYearStartMonth', Number(event.target.value))}
              hint="1 = January. Used to group financial reporting periods."
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Inventory rules" description="How the ledger behaves when stock runs out." />
          <CardBody className="space-y-4">
            <Select
              label="Costing method"
              value={form.costingMethod ?? 'AVERAGE'}
              onChange={(event) => update('costingMethod', event.target.value as 'AVERAGE' | 'FIFO')}
              hint="Moving weighted average is the default. Changing this affects future movements only."
            >
              <option value="AVERAGE">Moving weighted average</option>
              <option value="FIFO">First in, first out</option>
            </Select>

            <Checkbox
              label="Allow stock to go negative"
              description="Off by default. Turning this on lets a sale proceed with no stock on hand — useful for service and restaurant businesses."
              checked={form.allowNegativeStock ?? false}
              onChange={(event) => update('allowNegativeStock', event.target.checked)}
            />
            <Checkbox
              label="Tax is included in displayed prices"
              checked={form.taxInclusivePricing ?? false}
              onChange={(event) => update('taxInclusivePricing', event.target.checked)}
            />
            <Checkbox
              label="Stock transfers need approval"
              description="Transfers wait for a manager before stock leaves the source warehouse."
              checked={form.requireStockTransferApproval ?? true}
              onChange={(event) => update('requireStockTransferApproval', event.target.checked)}
            />
            <Checkbox
              label="Purchase orders need approval"
              checked={form.requirePurchaseApproval ?? true}
              onChange={(event) => update('requirePurchaseApproval', event.target.checked)}
            />
            <Checkbox
              label="Send low-stock alerts"
              checked={form.lowStockAlertsEnabled ?? true}
              onChange={(event) => update('lowStockAlertsEnabled', event.target.checked)}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Who gets reminders"
            description="Email addresses the system writes to when something needs chasing — a land instalment falling due, stock running out."
          />
          <CardBody className="space-y-4">
            <ReminderRecipients
              value={form.reminderEmails ?? []}
              onChange={(emails) => update('reminderEmails', emails)}
            />
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader
            title="Modules"
            description="Turn optional modules on or off. Modules outside your plan stay unavailable until you upgrade."
          />
          <CardBody>
            <div className="grid gap-3 sm:grid-cols-2">
              {OPTIONAL_FEATURES.map(([key, name, description]) => (
                <div key={key} className="flex items-start justify-between gap-3 rounded-lg border border-subtle p-3">
                  <div>
                    <p className="text-sm font-medium">{name}</p>
                    <p className="text-xs text-muted">{description}</p>
                  </div>
                  <input
                    type="checkbox"
                    aria-label={name}
                    className="mt-1 size-4 shrink-0 rounded border-subtle text-brand-600"
                    checked={enabled.has(key)}
                    onChange={(event) =>
                      void toggleFeature({ featureKey: key, enabled: event.target.checked })
                    }
                  />
                </div>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* Saved on the device rather than on the business, so it sits at the
            end: everything above it is the business, this is the screen. */}
        <div className="lg:col-span-2">
          <AppearanceCard />
        </div>

        <div className="lg:col-span-2">
          <PrinterCard />
        </div>
      </div>
    </>
  );
}

/**
 * The people a reminder is sent to.
 *
 * Addresses rather than accounts, because the person who chases land money is
 * often the one who never signs in — an accountant, a director, a shared
 * inbox. Left empty, the system falls back to the owners and whoever holds the
 * matching permission, so a business that never opens this screen still hears
 * about its own money.
 */
function ReminderRecipients({
  value,
  onChange,
}: {
  value: string[];
  onChange: (emails: string[]) => void;
}) {
  const [draft, setDraft] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const add = (): void => {
    const email = draft.trim().toLowerCase();
    if (!email) return;

    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setProblem(`"${draft.trim()}" is not an email address.`);
      return;
    }
    if (value.includes(email)) {
      setProblem('That address is already on the list.');
      return;
    }

    setProblem(null);
    setDraft('');
    onChange([...value, email]);
  };

  return (
    <>
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Input
            label="Add an address"
            type="email"
            placeholder="accounts@business.com"
            value={draft}
            error={problem ?? undefined}
            onChange={(event) => {
              setDraft(event.target.value);
              setProblem(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                add();
              }
            }}
          />
        </div>
        <Button variant="outline" onClick={add} disabled={!draft.trim()}>Add</Button>
      </div>

      {value.length === 0 ? (
        <p className="text-sm text-muted">
          Nobody listed. Reminders go to the owners and to anyone holding the matching permission.
        </p>
      ) : (
        <ul className="divide-y divide-[var(--border-subtle)] rounded-lg border border-subtle">
          {value.map((email) => (
            <li key={email} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="truncate text-sm">{email}</span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onChange(value.filter((entry) => entry !== email))}
                aria-label={`Remove ${email}`}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-muted">
        Nothing is sent until the email settings are filled in. Save this page, then use
        <span className="font-medium"> Send reminders </span>
        on Property sales to check it works.
      </p>
    </>
  );
}
